import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import actions from 'actions/index';
import selectors from 'selectors';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import SearchResult from 'components/SearchResult';
import SearchOverlay from 'components/SearchOverlay';
import Icon from 'components/Icon';
import getClassName from 'helpers/getClassName';
import DataElementWrapper from 'components/DataElementWrapper';
import { addSearchListener, removeSearchListener } from 'helpers/search';
import { isSpreadsheetEditorMode } from 'src/helpers/officeEditor';
import getDocument from 'src/core/getDocument';
import getRootNode from 'helpers/getRootNode';


import './SearchPanel.scss';
import useSearch from 'hooks/useSearch';

const propTypes = {
  isOpen: PropTypes.bool,
  isMobile: PropTypes.bool,
  pageLabels: PropTypes.array,
  currentWidth: PropTypes.number,
  closeSearchPanel: PropTypes.func,
  setActiveResult: PropTypes.func,
  isInDesktopOnlyMode: PropTypes.bool,
  isProcessingSearchResults: PropTypes.bool,
  activeDocumentViewerKey: PropTypes.number,
  isCustomPanel: PropTypes.bool,
};

function noop() { }

function SearchPanel(props) {
  const {
    isOpen,
    currentWidth,
    pageLabels,
    closeSearchPanel = noop,
    setActiveResult = noop,
    setNextResultValue = noop,
    isMobile = false,
    isInDesktopOnlyMode,
    isProcessingSearchResults,
    activeDocumentViewerKey,
    dataElement = 'searchPanel',
    isCustomPanel = false,
  } = props;

  const { t } = useTranslation();
  const { searchStatus, searchResults, activeSearchResultIndex, setSearchStatus, setActiveSearchResultIndex } = useSearch(activeDocumentViewerKey);
  const dispatch = useDispatch();
  const onCloseButtonClick = React.useCallback(function onCloseButtonClick() {
    if (closeSearchPanel) {
      closeSearchPanel();
    }
  }, [closeSearchPanel]);

  const onClickResult = React.useCallback(function onClickResult(resultIndex, result) {
    setActiveSearchResultIndex(resultIndex);
    setActiveResult(result);
    if (!isInDesktopOnlyMode && isMobile) {
      closeSearchPanel();
    }

    setNextResultValue(result);
  }, [closeSearchPanel, isMobile, setActiveResult, setActiveSearchResultIndex, isInDesktopOnlyMode, setNextResultValue]);

  const isSearchInProgress = useSelector((state) => selectors.isSearchInProgress(state));

  const searchEventListener = () => {
    dispatch(actions.setSearchInProgress(false));
  };

  const adjustSpreadsheetTableWidth = (isUnmounting) => {
    if (isSpreadsheetEditorMode()) {
      const editorWrapper = getRootNode().getElementById('editorWrapper');
      if (isUnmounting) {
        editorWrapper.style.removeProperty('width');
      } else {
        const newWidth = `${window.innerWidth - (currentWidth)}px`;
        editorWrapper.style.width = newWidth;
      }
      const editor = getDocument().getSpreadsheetEditorDocument().getEditor();
      editor.onSizeChanged();
    }
  };

  React.useEffect(() => {
    adjustSpreadsheetTableWidth();
  }, [currentWidth]);

  React.useEffect(() => {
    // componentDidMount
    adjustSpreadsheetTableWidth();
    addSearchListener(searchEventListener);
  }, []);

  React.useEffect(() => {
    // componentWillUnmount
    return () => {
      adjustSpreadsheetTableWidth(true);
      removeSearchListener(searchEventListener);
    };
  }, []);

  const className = getClassName('Panel SearchPanel', { isOpen });
  let style = {};
  if (!isCustomPanel && (isInDesktopOnlyMode || !isMobile)) {
    style = { width: `${currentWidth}px`, minWidth: `${currentWidth}px` };
  }

  return (
    <DataElementWrapper
      className={className}
      dataElement={dataElement}
      style={style}
    >
      {!isInDesktopOnlyMode && isMobile &&
        <div
          className="close-container"
        >
          <button
            className="close-icon-container"
            onClick={onCloseButtonClick}
          >
            <Icon
              glyph="ic_close_black_24px"
              className="close-icon"
            />
          </button>
        </div>}
      <SearchOverlay
        searchStatus={searchStatus}
        setSearchStatus={setSearchStatus}
        searchResults={searchResults}
        activeResultIndex={activeSearchResultIndex}
        isPanelOpen={isOpen}
        isSearchInProgress={isSearchInProgress}
        activeDocumentViewerKey={activeDocumentViewerKey}
      />
      <SearchResult
        t={t}
        searchStatus={searchStatus}
        searchResults={searchResults}
        activeResultIndex={activeSearchResultIndex}
        onClickResult={onClickResult}
        pageLabels={pageLabels}
        isProcessingSearchResults={isProcessingSearchResults}
        isSearchInProgress={isSearchInProgress}
      />
    </DataElementWrapper>
  );
}

SearchPanel.propTypes = propTypes;

export default SearchPanel;
